import { cn } from '@/lib/utils'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import Select from 'app/frontend/base/components/Select'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import CharacterRole, {
  characterRoles,
} from 'app/frontend/types/custom/CharacterRole'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import EventActivityGridRow from './EventActivityGridRow'
import {
  EventActivityParticipantEntry,
  EventActivityParticipantGroup,
} from './eventActivityParticipantEntry'
import {
  eventParticipantStatuses,
  presentParticipantStatuses,
  unavailableParticipantStatuses,
} from 'app/frontend/types/custom/EventParticipantStatus'
import capitalize from 'lodash/capitalize'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { useTeamEventPlanningInfoContext } from '../context/TeamEventPlanningInfoContext'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import EventActivityPlan from './EventActivityPlan'
import { useLocation } from 'react-use'
import EventActivityHeaderCell from './EventActivityHeaderCell'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import EventActivityList from './EventActivityList'
import ImportFromPreviousEventDialog from './ImportFromPreviousEventDialog'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'

type Props = {
  event: wowaudit.Event
  activities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  isRaidPlansLoading: boolean
}

type GridSortBy = 'name' | 'class'
type GridSortDirection = 'asc' | 'desc'

const roleSortOrder = new Map(
  characterRoles.map((role, index) => [role, index]),
)
const defaultActivityHeaderIssues = {
  selectedCount: 0,
  tooManyPlayers: false,
}
const participantColumnWidth = 190
const activityColumnMinWidth = 53
const activityColumnMaxWidth = 192

const EventActivityGrid = ({
  event,
  activities,
  linkedPlanDataByActivityId,
  isRaidPlansLoading,
}: Props) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const {
    data: { user },
  } = usePageInfo()
  const { showPlanningInfo } = useTeamEventPlanningInfoContext()
  const {
    updateEventActivityOrder,
    updateEvent,
    importEventFromPreviousEvent,
  } = useTeamEventUpdateContext()
  const { allEntries, activityIssuesById } = useTeamEventParticipantsContext()
  const [orderedActivityIds, setOrderedActivityIds] = useState<number[]>(
    activities.map((activity) => activity.id),
  )
  const { hash: initialPlannedActivityId } = useLocation()
  const [plannedActivityId, setPlannedActivityId] = useState<
    number | undefined
  >(Number(initialPlannedActivityId?.slice(1)) ?? undefined)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [sortBy, setSortBy] = useState<GridSortBy>('name')
  const [sortDirection, setSortDirection] = useState<GridSortDirection>('asc')
  const [containerWidth, setContainerWidth] = useState<number | undefined>()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canAdminEdit = accessLevel === 'admin'
  const selectableStatuses = useMemo(
    () =>
      eventParticipantStatuses.filter(
        (status) =>
          status !== 'removed' &&
          (canAdminEdit ||
            team.settings.raidsAvailableStatuses.value.includes(status)),
      ),
    [canAdminEdit, team.settings.raidsAvailableStatuses.value],
  )
  const sortOptions = useMemo(
    () => [
      { label: 'name', value: 'name' },
      { label: 'class', value: 'class' },
    ],
    [],
  )
  const stickyTop = useStickyTop()
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  )
  const orderedActivities = useMemo(() => {
    const activitiesById = new Map(
      activities.map((activity) => [activity.id, activity]),
    )
    const matchesCurrentActivities =
      orderedActivityIds.length === activities.length &&
      orderedActivityIds.every((activityId) => activitiesById.has(activityId))
    if (!matchesCurrentActivities) return activities
    return orderedActivityIds
      .map((activityId) => activitiesById.get(activityId))
      .filter((activity): activity is wowaudit.EventActivity => !!activity)
  }, [activities, orderedActivityIds])

  const handleActivityDragEnd = ({ active, over }: DragEndEvent) => {
    if (!canAdminEdit) return
    if (!over) return

    const activityIds = orderedActivities.map(
      (activity) => `event-activity-${activity.id}`,
    )
    const sourceIndex = activityIds.indexOf(String(active.id))
    const targetIndex = activityIds.indexOf(String(over.id))
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex)
      return

    const reordered = arrayMove(orderedActivities, sourceIndex, targetIndex)
    const reorderedIds = reordered.map((activity) => activity.id)
    setOrderedActivityIds(reorderedIds)
    void updateEventActivityOrder(reorderedIds).catch(() => {
      setOrderedActivityIds(activities.map((activity) => activity.id))
    })
  }

  const groupedRows = useMemo<EventActivityParticipantGroup[]>(() => {
    const compareRowsWithinRole = (
      left: EventActivityParticipantEntry,
      right: EventActivityParticipantEntry,
    ) => {
      const compareValue =
        sortBy === 'class'
          ? left.participant.characterReference.characterClass.localeCompare(
              right.participant.characterReference.characterClass,
            )
          : left.participant.characterReference.name.localeCompare(
              right.participant.characterReference.name,
            )
      const directionalValue =
        sortDirection === 'asc' ? compareValue : -compareValue
      if (directionalValue !== 0) return directionalValue
      return left.participant.characterReference.name.localeCompare(
        right.participant.characterReference.name,
      )
    }

    const sortRoleKeys = (roleKeys: string[]) =>
      roleKeys.slice().sort((leftRole, rightRole) => {
        const leftRoleOrder =
          roleSortOrder.get(leftRole as typeof characterRoles[number]) ??
          characterRoles.length
        const rightRoleOrder =
          roleSortOrder.get(rightRole as typeof characterRoles[number]) ??
          characterRoles.length

        if (leftRoleOrder !== rightRoleOrder) {
          return leftRoleOrder - rightRoleOrder
        }

        return leftRole.localeCompare(rightRole)
      })

    const toRoleGroups = (
      rows: EventActivityParticipantEntry[],
      isUnavailable: boolean,
    ): EventActivityParticipantGroup[] => {
      const rowsByRole = new Map<string, EventActivityParticipantEntry[]>()

      rows.forEach((row) => {
        const roleKey = row.role
        const existing = rowsByRole.get(roleKey) ?? []
        existing.push(row)
        rowsByRole.set(roleKey, existing)
      })

      return sortRoleKeys(Array.from(rowsByRole.keys())).map((roleKey) => ({
        key: isUnavailable ? 'unavailable' : roleKey,
        role: roleKey,
        isUnavailable,
        rows: (rowsByRole.get(roleKey) ?? []).sort(compareRowsWithinRole),
      }))
    }

    const availableGroups = toRoleGroups(
      allEntries.filter(
        (row) =>
          !unavailableParticipantStatuses.includes(row.participant.status),
      ),
      false,
    )
    const unavailableRows = allEntries
      .filter((row) =>
        unavailableParticipantStatuses.includes(row.participant.status),
      )
      .sort(compareRowsWithinRole)
    const unavailableGroups = unavailableRows.length
      ? [
          {
            key: 'unavailable',
            role: 'unavailable',
            isUnavailable: true,
            rows: unavailableRows,
          },
        ]
      : []

    return [...availableGroups, ...unavailableGroups]
  }, [allEntries, sortBy, sortDirection])
  const selectableParticipantCount = useMemo(
    () =>
      allEntries.filter(
        (entry) =>
          !unavailableParticipantStatuses.includes(entry.participant.status),
      ).length,
    [allEntries],
  )
  const selectedRoleCountByActivity = useMemo(() => {
    const counts = new Map<string, number>()
    allEntries.forEach((entry) => {
      if (unavailableParticipantStatuses.includes(entry.participant.status)) {
        return
      }
      orderedActivities.forEach((activity) => {
        const selection = entry.selections[activity.id]
        if (!selection?.selected) {
          return
        }
        const key = `${activity.id}:${selection.role ?? entry.role}`
        counts.set(key, (counts.get(key) ?? 0) + 1)
      })
    })
    return counts
  }, [orderedActivities, allEntries])
  const plannedActivity = orderedActivities.find(
    (activity) => activity.id === plannedActivityId,
  )
  const isPlanningMode = !!plannedActivity
  const isWidthKnown = typeof containerWidth === 'number'
  const estimatedActivityColumnWidth =
    orderedActivities.length > 0 && typeof containerWidth === 'number'
      ? (containerWidth - participantColumnWidth) / orderedActivities.length
      : Number.POSITIVE_INFINITY
  const activityColumnWidth = Number.isFinite(estimatedActivityColumnWidth)
    ? Math.min(estimatedActivityColumnWidth, activityColumnMaxWidth)
    : undefined
  const isCompactListMode =
    orderedActivities.length > 0 &&
    estimatedActivityColumnWidth < activityColumnMinWidth

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const updateWidth = () => {
      setContainerWidth(node.clientWidth)
    }

    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [])

  const handlePlanClick = (activityId: number) => {
    setPlannedActivityId((current) => {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${
          current === activityId ? '' : `#${activityId}`
        }`,
      )

      return current === activityId ? undefined : activityId
    })
  }
  const handlePlannedActivitySelect = (activityId: number) => {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}#${activityId}`,
    )
    setPlannedActivityId(activityId)
  }

  const table = (
    <table className='border-separate border-spacing-y-0.5 border-spacing-x-0.5'>
      <thead className='[&_th]:align-bottom'>
        <tr>
          <th
            className={cn(
              'sticky z-30 bg-card border-b font-normal text-xs w-[190px] min-w-[190px] max-w-[190px]',
              stickyTop,
            )}
          >
            <div className='mb-1 min-h-5'>
              {isPlanningMode ? (
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => {
                    window.history.replaceState(
                      window.history.state,
                      '',
                      window.location.pathname,
                    )
                    setPlannedActivityId(undefined)
                  }}
                  className='w-full'
                >
                  Back to selections
                </Button>
              ) : (
                <div className='flex gap-2 items-center'>
                  <div>
                    {sortDirection === 'asc' ? (
                      <ArrowUpIcon
                        className='w-4 h-4 cursor-pointer'
                        onClick={() => setSortDirection('desc')}
                      />
                    ) : (
                      <ArrowDownIcon
                        className='w-4 h-4 cursor-pointer'
                        onClick={() => setSortDirection('asc')}
                      />
                    )}
                  </div>

                  <div className='flex items-center gap-1'>
                    <span className='text-muted-foreground'>sort by</span>
                    <Select
                      value={sortBy}
                      setValue={(value) => {
                        if (!value) return
                        setSortBy(value as GridSortBy)
                      }}
                      options={sortOptions}
                      hideIcon
                      className='bg-transparent hover:bg-transparent border-0 w-fit font-normal text-xs p-0 h-5'
                      placeholder='Sort by'
                      optionWidth='fit'
                    />
                  </div>
                </div>
              )}
            </div>
          </th>

          <SortableContext
            items={orderedActivities.map(
              (activity) => `event-activity-${activity.id}`,
            )}
            strategy={horizontalListSortingStrategy}
          >
            {orderedActivities.map((activity) => {
              const issues =
                activityIssuesById.get(activity.id) ??
                defaultActivityHeaderIssues
              const linkedPlanData = linkedPlanDataByActivityId.get(activity.id)
              const allSelectedForActivity =
                selectableParticipantCount > 0 &&
                issues.selectedCount === selectableParticipantCount
              return (
                <EventActivityHeaderCell
                  key={activity.id}
                  activity={activity}
                  activities={orderedActivities}
                  stickyTop={stickyTop}
                  activityColumnWidth={activityColumnWidth}
                  canAdminEdit={canAdminEdit}
                  allSelectedForActivity={allSelectedForActivity}
                  issues={issues}
                  isPlannedActivity={plannedActivity?.id === activity.id}
                  isPlanningMode={isPlanningMode}
                  hasLinkedPlanIssues={
                    linkedPlanData?.hasLinkedPlanIssues ?? false
                  }
                  linkedPlanIssueCount={
                    linkedPlanData?.linkedPlanIssueCount ?? 0
                  }
                  onPlanClick={handlePlanClick}
                />
              )
            })}
          </SortableContext>
        </tr>
      </thead>

      {isPlanningMode
        ? null
        : groupedRows.map((group, i) => (
            <tbody key={group.key}>
              <tr aria-hidden>
                <td className='border-0 p-0'>
                  <div
                    className={cn(
                      'flex items-center gap-1.5 text-xs',
                      i > 0 && showPlanningInfo ? 'mt-8' : 'mt-1',
                    )}
                  >
                    <CharacterRoleIcon
                      role={group.role as CharacterRole}
                      size='small'
                    />
                    {capitalize(group.role)}
                    <span className='text-muted-foreground flex items-center gap-[2px]'>
                      {group.isUnavailable ? null : (
                        <span>
                          {
                            group.rows.filter((row) =>
                              presentParticipantStatuses.includes(
                                row.participant.status,
                              ),
                            ).length
                          }
                        </span>
                      )}
                      {group.isUnavailable ? null : <span>/</span>}
                      <span>{group.rows.length}</span>
                    </span>
                  </div>
                </td>
                {orderedActivities.map((activity) => {
                  const selectedCount = group.isUnavailable
                    ? ''
                    : selectedRoleCountByActivity.get(
                        `${activity.id}:${group.role}`,
                      ) ?? 0
                  return (
                    <td
                      key={`${group.key}-${activity.id}`}
                      className={cn(
                        'border-0 p-0 pt-1 text-center text-xs font-mono',
                        i > 0 && showPlanningInfo ? 'pt-8' : 'pt-1',
                      )}
                      style={{
                        width: activityColumnWidth,
                        maxWidth: activityColumnWidth,
                      }}
                    >
                      {group.isUnavailable || !activity.active ? null : (
                        <span className='flex items-center gap-1 justify-center'>
                          <CharacterRoleIcon
                            role={group.role as CharacterRole}
                            size='small'
                          />
                          {selectedCount}
                        </span>
                      )}
                    </td>
                  )
                })}
              </tr>

              {group.rows.map((row) => (
                <EventActivityGridRow
                  key={row.participant.id}
                  group={group}
                  row={row}
                  activities={orderedActivities}
                  selectableStatuses={selectableStatuses}
                  canAdminEdit={canAdminEdit}
                  userId={user?.id}
                />
              ))}
            </tbody>
          ))}
    </table>
  )

  const plannedActivityIssues = plannedActivity
    ? activityIssuesById.get(plannedActivity.id) ?? defaultActivityHeaderIssues
    : defaultActivityHeaderIssues
  const allSelectedForPlannedActivity =
    !!plannedActivity &&
    selectableParticipantCount > 0 &&
    plannedActivityIssues.selectedCount === selectableParticipantCount

  return (
    <div ref={containerRef}>
      {canAdminEdit && !event.touched ? (
        <Card className='mb-3 flex items-center justify-center w-full gap-6'>
          <h4 className='m-0'>Get started</h4>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              onClick={() => {
                setIsImportDialogOpen(true)
              }}
            >
              Import from previous event
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                void updateEvent({ touched: true })
              }}
            >
              Start from scratch
            </Button>
          </div>
        </Card>
      ) : null}
      <Card
        className={cn(
          'pt-0 transition-opacity',
          isWidthKnown ? 'opacity-100' : 'opacity-0',
        )}
      >
        {!isWidthKnown ? null : isCompactListMode ? (
          <EventActivityList
            activities={orderedActivities}
            selectedActivityId={plannedActivityId}
            onSelectedActivityIdChange={handlePlannedActivitySelect}
            selectableParticipantCount={selectableParticipantCount}
            activityIssuesById={activityIssuesById}
            linkedPlanDataByActivityId={linkedPlanDataByActivityId}
            isRaidPlansLoading={isRaidPlansLoading}
          />
        ) : (
          <>
            {canAdminEdit ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleActivityDragEnd}
              >
                {table}
              </DndContext>
            ) : (
              table
            )}
            {plannedActivity ? (
              <EventActivityPlan
                activity={plannedActivity}
                activities={orderedActivities}
                allSelectedForActivity={allSelectedForPlannedActivity}
                linkedPlanData={linkedPlanDataByActivityId.get(
                  plannedActivity.id,
                )}
                isLinkedPlanDataLoading={isRaidPlansLoading}
              />
            ) : null}
          </>
        )}
      </Card>
      <ImportFromPreviousEventDialog
        event={event}
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImport={async (sourceEventId) => {
          await importEventFromPreviousEvent(sourceEventId)
        }}
      />
    </div>
  )
}

export default EventActivityGrid
